import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  InfoCircle,
  StatusAlert,
  StatusComplete,
  StatusWarning,
} from '@wix/wix-ui-icons-common';

import Text from '../Text';
import Button from '../Button';
import CloseButton from '../CloseButton';
import { SKIN, dataHooks, LAYOUT, SIZE, BORDER } from './constants';
import { classes, st } from './SectionHelper.st.css';
import { SectionHelperSkin, SectionHelperProps } from './SectionHelper.types';
import deprecationLog from '../utils/deprecationLog';

const DEFAULT_ICONS: Record<SectionHelperSkin, React.ReactNode> = {
  [SKIN.Standard]: <InfoCircle />,
  [SKIN.Success]: <StatusComplete />,
  [SKIN.Warning]: <StatusWarning />,
  [SKIN.Danger]: <StatusAlert />,
  [SKIN.Premium]: <InfoCircle />,
  [SKIN.Preview]: <InfoCircle />,
  [SKIN.ExperimentalDark]: <InfoCircle />,
};

const SectionHelper = ({
  dataHook,
  showCloseButton = false,
  onClose,
  onAction,
  actionText,
  appearance = SKIN.Warning, // TODO: add default to skin prop once appearance is removed
  title,
  size = SIZE.Medium,
  fullWidth = false,
  children,
  layout = LAYOUT.Vertical,
  border = BORDER.Standard,
  showPrefixIcon = false,
  prefixIcon,
  ...props
}: SectionHelperProps) => {
  const skin = props.skin || appearance;
  const isExperimentalDark = skin === SKIN.ExperimentalDark;
  const icon = showPrefixIcon && (prefixIcon ?? DEFAULT_ICONS[skin]);

  useEffect(() => {
    if (appearance) {
      deprecationLog(
        '<SectionHelper/> - prop "appearance" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
  }, [appearance]);

  return (
    <div
      data-hook={dataHook}
      data-skin={skin}
      className={st(classes.root, {
        skin,
        withCloseBtn: showCloseButton,
        fullWidth,
        size,
        border,
        hasPrefix: showPrefixIcon,
      })}
    >
      {showCloseButton && (
        <div className={st(classes.close, { size })}>
          <CloseButton
            dataHook={dataHooks.closeButton}
            size="medium"
            skin={isExperimentalDark ? 'light' : 'dark'}
            onClick={onClose}
          />
        </div>
      )}

      {icon && (
        <Text
          dataHook={dataHooks.prefix}
          className={st(classes.prefix)}
          light={isExperimentalDark}
        >
          {icon}
        </Text>
      )}

      <div className={st(classes.content, { size, layout })}>
        <div className={st(classes.textContainer)}>
          {title && (
            <div className={classes.title}>
              <Text
                className={classes.text}
                light={isExperimentalDark}
                dataHook={dataHooks.title}
                size="small"
                weight="bold"
              >
                {title}
              </Text>
            </div>
          )}

          <Text
            className={classes.text}
            size="small"
            light={isExperimentalDark}
          >
            {children}
          </Text>
        </div>

        {actionText && (
          <Button
            className={st(classes.action)}
            size={size === 'small' ? 'tiny' : 'small'}
            skin={isExperimentalDark ? 'standard' : 'dark'}
            priority={isExperimentalDark ? 'primary' : 'secondary'}
            onClick={onAction}
            dataHook={dataHooks.actionButton}
          >
            {actionText}
          </Button>
        )}
      </div>
    </div>
  );
};

SectionHelper.displayName = 'SectionHelper';

SectionHelper.propTypes = {
  dataHook: PropTypes.string,
  appearance: PropTypes.oneOf([
    'warning',
    'standard',
    'danger',
    'success',
    'premium',
    'preview',
    'experimentalDark',
  ]),
  skin: PropTypes.oneOf([
    'warning',
    'standard',
    'danger',
    'success',
    'premium',
    'preview',
    'experimentalDark',
  ]),
  title: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium']),
  showCloseButton: PropTypes.bool,
  onClose: PropTypes.func,
  onAction: PropTypes.func,
  actionText: PropTypes.string,
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
  layout: PropTypes.oneOf(['vertical', 'horizontal']),
  border: PropTypes.oneOf(['standard', 'topBottom']),
  showPrefixIcon: PropTypes.any,
  prefixIcon: PropTypes.any,
};

export default SectionHelper;
